import { ChangeDetectionStrategy, Component, Signal, input, output } from '@angular/core';
import { WaitingOpponentsData } from 'src/app/core/api/player/schemas/waiting-opponents';
import { FreeMatchTierData } from 'src/app/core/api/player/schemas/tiers';
import { SoloTierData } from 'src/app/core/api/player/schemas/solo-tiers';
import {
  DuelPaymentEventData,
  FreeMatchPlayEventData,
  TournamentPaymentEventData,
} from 'src/app/core/services/tiers/models/payment-events';
import type { DuelTierDataWithMeta } from 'src/app/core/services/tiers/tiers.service';
import { TournamentDataModel } from 'src/app/core/services/tournament/models/tournament-data-model';
import { WagerTierListComponent } from 'src/app/features/wager/components/wager-tier-list/wager-tier-list.component';
import { ButtonComponent } from 'src/app/ui/buttons/button/button.component';
import { CountdownPillComponent } from 'src/app/ui/pills/countdown-pill/countdown-pill.component';

// 'pending' until the unfinished-room check has run, then the tier overlay or, with a room to
// finish, the continue-game footer.
export type TierFirstContent = 'pending' | 'tiers' | 'continue-game';

@Component({
  selector: 'app-tier-first-controls-view',
  templateUrl: './tier-first-controls-view.component.html',
  styleUrls: ['./tier-first-controls-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WagerTierListComponent, CountdownPillComponent, ButtonComponent],
})
export class TierFirstControlsViewComponent {
  // Derived once by the container, which also tells the shell whether the overlay covers the game.
  readonly content = input.required<TierFirstContent>();
  // Whether the theme makes the overlay see-through to the app background.
  readonly showsAppBackground = input.required<boolean>();
  // The tier list takes these five as signals, so they pass through unread.
  readonly allTiersSignal = input.required<Signal<DuelTierDataWithMeta[]>>();
  readonly freeMatchesSignal = input.required<Signal<FreeMatchTierData[]>>();
  readonly currentTournamentDataSignal = input.required<Signal<TournamentDataModel | undefined>>();
  readonly soloTiersSignal = input.required<Signal<SoloTierData[]>>();
  readonly waitingOpponentsStandard = input.required<Signal<WaitingOpponentsData | undefined>>();
  readonly paymentInFlight = input.required<boolean>();
  // Required for the tier list's reason: unbound, every wager card stays tappable whatever it costs.
  readonly userBalance = input.required<number | undefined>();
  readonly formattedBalance = input<string | undefined>();
  readonly canShowLocal = input.required<boolean>();
  readonly isMarbles = input.required<boolean>();
  readonly unfinishedTimeRemaining = input.required<string>();
  readonly continueButtonLabel = input.required<string>();

  readonly wagerClickedEvent = output<DuelPaymentEventData>();
  readonly tournamentPlayClickedEvent = output<TournamentPaymentEventData>();
  readonly tutorialRequestedEvent = output<TournamentDataModel>();
  readonly leaderboardRequestedEvent = output<TournamentDataModel>();
  readonly freeMatchClickedEvent = output<FreeMatchPlayEventData>();
  readonly soloClickedEvent = output<void>();
  readonly practiceClickedEvent = output<void>();
  readonly localClickedEvent = output<void>();
  readonly celebrationClearedEvent = output<void>();
  readonly continueGameClickedEvent = output<void>();

  onWagerClick(data: DuelPaymentEventData): void {
    this.wagerClickedEvent.emit(data);
  }

  onTournamentPlayClick(data: TournamentPaymentEventData): void {
    this.tournamentPlayClickedEvent.emit(data);
  }

  onTutorialRequested(tournamentData: TournamentDataModel): void {
    this.tutorialRequestedEvent.emit(tournamentData);
  }

  onLeaderboardRequested(tournamentData: TournamentDataModel): void {
    this.leaderboardRequestedEvent.emit(tournamentData);
  }

  onFreeMatchClick(data: FreeMatchPlayEventData): void {
    this.freeMatchClickedEvent.emit(data);
  }

  onSoloClick(): void {
    this.soloClickedEvent.emit();
  }

  onPracticeClick(): void {
    this.practiceClickedEvent.emit();
  }

  onLocalClick(): void {
    this.localClickedEvent.emit();
  }

  onCelebrationCleared(): void {
    this.celebrationClearedEvent.emit();
  }

  continueGameClicked(): void {
    this.continueGameClickedEvent.emit();
  }
}
